// =============================================================================
// io/sync/saveAccount.ts — the only thing in this client that writes an account.
// -----------------------------------------------------------------------------
// ── 🚨 `extras` IS ONE JSONB BLOB AND FIVE REAL FIELDS LIVE IN IT ──
//
// `excludeFromMetrics`, `archived`, `feeWaived`, `feeCadence` and `feeAmount`
// have NO COLUMNS. Nor do `consistencyScope` and `dllEnforcement`, which ride
// `extras.rulesX`. The old client also stores `setupNudgeDismissed` there, and
// `extras` is explicitly the forward-compatible bag — so a future key can appear
// in it that this client has never heard of.
//
// A writer that builds `{ excludeFromMetrics: … }` from the form therefore does
// not update one flag. It REPLACES the blob and silently deletes the rest: the
// archived state, the cost of the account, the consistency scope, and whatever
// the shipping client has written since. That is exactly the shape D110 dealt
// with on the preferences blob, where sixteen sibling keys were one careless
// object literal away from being erased.
//
// So this file spreads what is STORED, at two levels, and changes only the one
// key the form owns.
//
// ── 🚨 IT RE-READS THE ROW AT SAVE TIME, NOT AT FORM-OPEN TIME ──
//
// Merging over a blob that was read when the modal opened is merging over a
// stale copy: anything written in between — by the other client, by an import,
// by the realtime ingest — is inside the window and gets reverted. The read is
// the first thing `saveAccount` does.
//
// It also answers the question a PostgREST update cannot: an UPDATE against a
// row RLS hides SUCCEEDS with an empty result. Without the pre-read, deleting an
// account elsewhere and then saving it here would report success and write
// nothing.
//
// ── ⚠ WHAT THIS WRITER DELIBERATELY DOES NOT TOUCH ──
//
//   archived, feeWaived, feeCadence, feeAmount   no field in the form (D62)
//   lockMechanic, lockPad, dllEnforcement,       no field either — carried by
//     consistencyScope                           `mergeAccountRules`
//   source                                       template lineage; templates are
//                                                not ported
//   created_at / updated_at                      defaults and a trigger own them
//   the commission table                         FIRM-WIDE. Not this slice.
//   account_adjustments                          a different table, and a Reset
//                                                fee row moves the compliance
//                                                window
//
// 🔴 ONE EXCEPTION, ADDED WITH THE FEE FIELD (#73): this save DOES write the
// account's own fee row. It has to. `extras.feeAmount` is not what Economics
// reads — it reads `account_adjustments` — so a form that set the amount and
// stopped would clear the "Finish setup" nudge while the cost never reached the
// report. That is worse than the dead end it replaced: a confident ROI against a
// cost nobody recorded. The rule for WHICH row, and why it is amended rather
// than appended, is `domain/accountFee.ts` — one real $99 fee once landed as
// $287.
//
// ── ⚠ A SAVE NORMALISES NULL TO EMPTY, AND THAT IS PARITY, NOT A BUG ──
//
// `payout_schedule`, `scaling_rules` and `eod_flat_rule` are NULL on rows that
// have never been through this form. A save writes `''`, `''` and `false`,
// because the typed model says those fields are a string and a boolean, and the
// shipping client's own handler writes exactly the same three values from the
// same three empty inputs. Confirmed against Josh's real Sim1 row: one intended
// change, three null-to-empty normalisations, twenty-five columns untouched.
// Worth stating because a first save on any account shows four changed columns
// rather than one, which looks like a leak until you know why.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { feeStateFrom, planFeeAdjustment, type FeePlan } from '../../domain/accountFee';
import {
  mergeAccountRules, ownedAccountFields, type AccountDraft,
} from '../../domain/accountForm';
import { ACCOUNT_RULES_COLUMNS, type Account, type IsoDate } from '../../domain/types';
import { reportFailureAs } from '../diagnostics/errorLog';

export interface SaveAccountResult {
  ok: boolean;
  error: string | null;
  /** The extras blob as written, so a dry run can be shown to a human. */
  extras?: Record<string, unknown>;
  /** The column patch as written, same reason. */
  columns?: Record<string, unknown>;
  /**
   * What happened to the fee's ledger row, in the user's words — "fee logged",
   * "fee corrected, 2 duplicates removed". Null when nothing moved.
   *
   * ⚠ Worth saying out loud, because the row is the ONLY thing Economics reads.
   * A save that silently repaired a $287 total should say so.
   */
  feeMessage?: string | null;
  /**
   * 🔴 The fee row failed to write, THOUGH THE ACCOUNT SAVED (`ok: true`).
   *
   * Separate from `error` on purpose: the edit landed, so sending the trader back
   * to redo it would be wrong — but the cost did not reach the ledger, and that
   * silence is Economics reporting an ROI against a cost nobody recorded.
   */
  feeError?: string | null;
}

/** Rules keys that have a real column. The rest ride `extras.rulesX`. */
const RULES_COLUMN_ENTRIES = Object.entries(ACCOUNT_RULES_COLUMNS)
  .filter((e): e is [string, string] => typeof e[1] === 'string');

/**
 * The column patch — every field this form owns, and nothing else.
 *
 * ⚠ `current_balance` is resolved rather than taken from the draft. With
 * auto-calc ON the input is disabled and holds whatever was last computed, which
 * on Josh's Sim1 is the float artefact `50226.25000000001`. Writing that back
 * persists a rendering bug as data. Auto-calc means the stored figure is not
 * read by anything anyway — `accountDisplayBalance` recomputes it — so the
 * PRIOR value is kept untouched, and only an explicitly-edited balance is sent.
 */
export function accountColumnPatch(
  draft: AccountDraft, prior: Account,
): Record<string, unknown> {
  const f = ownedAccountFields(draft);
  const rules = mergeAccountRules(draft, prior.rules);

  const patch: Record<string, unknown> = {
    name: f.name,
    firm: f.firm,
    account_type: f.accountType,
    account_size: f.accountSize,
    starting_balance: f.startingBalance,
    current_balance: f.autoBalance ? prior.currentBalance : f.currentBalance,
    auto_balance: f.autoBalance,
    status: f.status,
    external_id: f.externalId,
    initial_peak_balance: f.initialPeakBalance,
  };

  for (const [key, col] of RULES_COLUMN_ENTRIES) {
    patch[col] = (rules as unknown as Record<string, unknown>)[key];
  }
  return patch;
}

/**
 * The extras blob, merged over what is stored.
 *
 * ⚠ THE SPREAD IS THE WHOLE MECHANISM, and it carries `rulesX` — the sub-object
 * holding `consistencyScope` and `dllEnforcement`, the two rules with no column
 * — along with `archived`, the three cost keys, `setupNudgeDismissed`, and any
 * key the shipping client has written that this one has never heard of. Only
 * `excludeFromMetrics` is the form's to change.
 *
 * ⚠ THERE WAS A SECOND, EXPLICIT `rulesX` SPREAD HERE AND IT DID NOTHING. It
 * re-copied what the line above had already carried, and deleting it changed no
 * behaviour and broke no test — which is how it was found, by a sabotage that
 * survived. A defensive line that cannot change the outcome is worse than
 * absent: it reads as the thing keeping `rulesX` alive, so a later edit could
 * "simplify" the outer spread believing that sub-object was handled separately.
 * It is not. This one line is.
 */
export function accountExtrasPatch(
  draft: AccountDraft, storedExtras: Record<string, unknown>,
): Record<string, unknown> {
  // ⚠ THE THREE COST FIELDS RIDE `extras` — none has a column, and the rule that
  // keeps `feeWaived` and `feeAmount` mutually exclusive lives in
  // `domain/accountFee.ts`. Spreading them from one place means a form cannot
  // save an account that is both free and $165.
  const fee = feeStateFrom({ amount: draft.feeAmount, cadence: draft.feeCadence });
  return {
    ...storedExtras,
    excludeFromMetrics: draft.excludeFromMetrics,
    ...fee,
  };
}

export interface SaveAccountOptions {
  /** Build the patch and return it WITHOUT writing. Used to prove a save. */
  dryRun?: boolean;
  /**
   * Today, for a fee row that has to be dated.
   *
   * ⚠ PASSED IN rather than read from the clock, so a monthly fee's date-matching
   * is testable at a month boundary. Omitted, the fee row is left alone — the
   * account still saves.
   */
  today?: string;
}

export async function saveAccount(
  sb: SupabaseClient,
  prior: Account,
  draft: AccountDraft,
  opts: SaveAccountOptions = {},
): Promise<SaveAccountResult> {
  if (!prior?.id) return { ok: false, error: 'Refusing to save an account with no id.' };
  if (!draft.name.trim()) return { ok: false, error: 'A name is required.' };

  // ── 1. The freshest extras, read now rather than when the form opened ──
  const { data, error: readError } = await sb
    .from('accounts')
    .select('extras')
    .eq('id', prior.id)
    .maybeSingle();

  if (readError) {
    return {
      ok: false,
      error: reportFailureAs(
        'save account',
        readError,
        `Couldn’t read the account before saving: ${readError.message}`,
      ),
    };
  }
  if (!data) {
    // ⚠ NOT a silent success. The row is gone, or RLS hides it — either way an
    // UPDATE would return an empty result and look like it worked.
    return {
      ok: false,
      error: 'That account is no longer there. Nothing was changed — reload before editing it again.',
    };
  }

  const storedExtras = ((data as Record<string, unknown>)['extras'] ?? {}) as Record<string, unknown>;
  const columns = accountColumnPatch(draft, prior);
  const extras = accountExtrasPatch(draft, storedExtras);

  if (opts.dryRun) return { ok: true, error: null, columns, extras };

  // ── 2. The write ──
  const { data: written, error } = await sb
    .from('accounts')
    .update({ ...columns, extras })
    .eq('id', prior.id)
    .select('id');

  if (error) {
    return {
      ok: false,
      error: reportFailureAs('save account', error, error.message),
      columns,
      extras,
    };
  }
  if (!written || !written.length) {
    return {
      ok: false,
      error: 'The save matched no rows — nothing was written. Reload and try again.',
      columns,
      extras,
    };
  }

  // ── 3. The fee's own ledger row ──
  //
  // 🔴 SEPARATE FROM THE ACCOUNT WRITE, AND A FAILURE HERE IS NOT A FAILED SAVE.
  // The account IS saved; reporting failure would send the trader back to redo an
  // edit that already landed. Same call `createAccount` makes about its fee
  // insert, and for the same reason — but the warning still has to reach them,
  // because a silent miss is Economics reporting an ROI against a cost that was
  // never recorded.
  if (!opts.today) return { ok: true, error: null, columns, extras };

  const plan = planFeeAdjustment({
    accountType: prior.accountType,
    firm: draft.firm,
    fee: feeStateFrom({ amount: draft.feeAmount, cadence: draft.feeCadence }),
    today: opts.today as IsoDate,
    existing: prior.adjustments ?? [],
  });

  const feeError = await applyFeePlan(sb, prior.id, userIdOf(prior), plan);
  return {
    ok: true,
    error: null,
    columns,
    extras,
    feeMessage: plan.message,
    feeError,
  };
}

/** The account's owner, for the insert. RLS scopes the read; the write needs it. */
function userIdOf(a: Account): string {
  return String((a as unknown as Record<string, unknown>)['userId'] ?? '');
}

/**
 * Apply a fee plan to `account_adjustments`.
 *
 * ⚠ REMOVE FIRST, THEN AMEND, THEN INSERT. If an insert ran before the removes
 * and then a remove failed, the ledger would hold BOTH the new row and the
 * duplicates this plan exists to collapse — the $287 state, arrived at by a
 * repair. Doing the destructive half first means a partial failure leaves fewer
 * rows than intended rather than more.
 */
async function applyFeePlan(
  sb: SupabaseClient, accountId: string, userId: string, plan: FeePlan,
): Promise<string | null> {
  if (plan.remove.length) {
    const { error } = await sb.from('account_adjustments').delete().in('id', plan.remove);
    if (error) return reportFailureAs('save account fee', error, error.message);
  }
  if (plan.amend) {
    const { error } = await sb.from('account_adjustments')
      .update({ amount: plan.amend.amount, note: plan.amend.note })
      .eq('id', plan.amend.id);
    if (error) return reportFailureAs('save account fee', error, error.message);
  }
  if (plan.insert) {
    // 🚨 THE ID IS MINTED HERE. `account_adjustments.id` is TEXT PRIMARY KEY with
    // NO DEFAULT, so an insert that omits it is rejected outright — which is how
    // one real $70 fee went missing from a newly created account. See
    // `createAccount`.
    const { error } = await sb.from('account_adjustments').insert({
      id: newAdjustmentId(),
      account_id: accountId,
      user_id: userId,
      date: plan.insert.date,
      type: plan.insert.type,
      amount: plan.insert.amount,
      note: plan.insert.note,
    });
    if (error) return reportFailureAs('save account fee', error, error.message);
  }
  return null;
}

/** Same shape as every other id this client mints. */
function newAdjustmentId(): string {
  return `adj_${crypto.randomUUID()}`;
}
